export interface BillSponsor {
  name: string;
  title: string;
  type: string;
  sponsor_type: string;
  party: string;
  legislator_id?: string;
}

export interface BillCommittee {
  name: string;
  chamber: string;
}

export interface BillAmendment {
  date: string;
  count: number;
}

export interface BillAmendmentDetail {
  amendment_number: string;
  url: string;
  date: string;
  status: string;
}

export interface NextCalendarEvent {
  calendar_id: number;
  reading_date: string;
  reading_time?: string;
  reading_title: string;
  room_number?: string;
  derived_location?: string;
  chamber?: string;
  upon_adjournment?: boolean;
}

export interface Bill {
  // Standard bill properties
  id: string;
  billNumber: string;
  title: string;
  concerning?: string;
  session: string;
  subject: string | string[];
  summary: string;
  status: string;
  lastActivity: string;
  location: string;
  introducedDate?: string | null;
  lastAmendment?: BillAmendment | null;
  amendments?: BillAmendmentDetail[];
  sponsors: BillSponsor[];
  committees: BillCommittee[];
  categories: string[];
  
  // Workspace-specific properties (optional - only present when workspace_id is provided)
  billPosition?: string;
  isHot?: boolean;
  notes?: string;
  calendarEvents?: any[]; // Various calendar event formats used across reports
  nextCalendarEvent?: NextCalendarEvent | null;
  trackedAt?: string;
  trackedBy?: string;
  workspaceName?: string;
  workspaceNames?: string[]; // For dashboard view with multiple workspaces
  workspaceIds?: string[]; // Workspace IDs corresponding to workspaceNames
  workspaces?: Array<{ name: string; id: string; position: string }>; // For org-level reports (one entry per tracking workspace)
  customFieldCount?: number; // Count of custom fields for this bill in the workspace

  /**
   * My Saved Views sub-records (#1144): present when the request named
   * view_workspace_ids. Feature-owned — deliberately NOT the report-owned
   * `workspaces` array above, which is expected to go away with reporting.
   */
  billWorkspaceRecords?: import('./savedViews').BillWorkspaceRecord[];
  
  // AI Summary (optional - only present when AI summary feature is enabled)
  aiShortSummary?: string | null;
}

export type BillDisplayContext = 'legislation' | 'workspace' | 'hot-bills-report' | 'bill-tracking-report' | 'org-hot-bills-report' | 'org-bill-tracking-report';

export interface BillFetchParams {
  search?: string;
  chamber?: string;
  /* Multi-select filters (#1209) accept one value or many; axios serializes
     arrays as repeated params, which the API expects. */
  session?: string | string[];
  subject?: string | string[];
  status?: string | string[];
  sponsor?: string | string[];
  committee?: string;
  committee_id?: string | string[];
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  includeIntroduced?: boolean;
  includeAmended?: boolean;
  startDate?: string;
  endDate?: string;
  workspace_id?: string;
  /* My Saved Views (#1144): sub-record fetching. */
  view_workspace_ids?: string[];
  view_custom_field_ids?: string[];
  tracked_bills_only?: boolean;
}

export interface BillsResponse {
  success: boolean;
  data: {
    bills: Bill[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    currentPage: number;
    totalPages: number;
  };
}

export interface WorkspacePermissions {
  canView: boolean;
  canEdit: boolean;
  canDelete?: boolean;
  canManage?: boolean;
}

export const BILL_POSITIONS = [
  { value: 'Monitor', label: 'Monitor' },
  { value: 'Support', label: 'Support' },
  { value: 'StrSupport', label: 'Strong Support' },
  { value: 'Neutral', label: 'Neutral' },
  { value: 'Oppose', label: 'Oppose' },
  { value: 'StrOppose', label: 'Strong Oppose' },
  { value: 'Amend', label: 'Amend' },
  { value: 'Pending', label: 'Pending' }
] as const;

export type BillPosition = typeof BILL_POSITIONS[number]['value'];
